import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useShallow } from 'zustand/react/shallow';
import { useSectionStore } from '../../store/sectionStore';
import {
  gizmoSize,
  mineToScene,
  sectionCenterScene,
  sectionNormal,
  sectionPlanes,
} from '../../Utils/sectionMath';
import { getActiveObjectClipBake } from '../../Utils/objectClip';
import { getOrigin } from '../../config/siteConfig';

const skip = { mdvSkipPick: true, mdvSkipClip: true, mdvSectionGizmo: true };

function CapPlane({ plane, size, opacity, color }) {
  const coplanar = new THREE.Vector3();
  plane.coplanarPoint(coplanar);
  const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), plane.normal);
  return (
    <mesh position={coplanar} quaternion={quat} userData={skip} raycast={() => false} frustumCulled={false}>
      <planeGeometry args={[size, size]} />
      <meshBasicMaterial
        color={color}
        transparent
        opacity={opacity}
        side={THREE.DoubleSide}
        depthWrite={false}
        polygonOffset
        polygonOffsetFactor={-1}
        clippingPlanes={[]}
      />
    </mesh>
  );
}

function EdgeBox({ center, normal, width, size }) {
  const geometry = useMemo(() => {
    const half = Math.max(0.05, width) / 2;
    const n = normal.clone().normalize();
    const helper = Math.abs(n.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
    const u = new THREE.Vector3().crossVectors(n, helper).normalize();
    const v = new THREE.Vector3().crossVectors(n, u).normalize();
    const s = size / 2;
    const corners = [];
    [-1, 1].forEach((sn) => {
      [-1, 1].forEach((su) => {
        [-1, 1].forEach((sv) => {
          corners.push(
            center
              .clone()
              .addScaledVector(n, sn * half)
              .addScaledVector(u, su * s)
              .addScaledVector(v, sv * s)
          );
        });
      });
    });
    const idx = [
      [0, 1], [2, 3], [0, 2], [1, 3],
      [4, 5], [6, 7], [4, 6], [5, 7],
      [0, 4], [1, 5], [2, 6], [3, 7],
    ];
    const pos = [];
    idx.forEach(([a, b]) => {
      pos.push(corners[a].x, corners[a].y, corners[a].z, corners[b].x, corners[b].y, corners[b].z);
    });
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    return g;
  }, [center, normal, width, size]);

  return (
    <lineSegments geometry={geometry} userData={skip} raycast={() => false} frustumCulled={false}>
      <lineBasicMaterial color="#eab308" />
    </lineSegments>
  );
}

function ObjectClipBounds({ visible }) {
  const ref = useRef();
  useFrame(() => {
    const mesh = ref.current;
    if (!mesh) return;
    const bake = getActiveObjectClipBake();
    if (!visible || !bake) {
      mesh.visible = false;
      return;
    }
    const { min, max } = bake;
    mesh.visible = true;
    mesh.position.set((min.x + max.x) * 0.5, (min.y + max.y) * 0.5, (min.z + max.z) * 0.5);
    mesh.scale.set(Math.max(max.x - min.x, 0.1), Math.max(max.y - min.y, 0.1), Math.max(max.z - min.z, 0.1));
  });
  return (
    <mesh ref={ref} userData={skip} raycast={() => false} visible={false} frustumCulled={false}>
      <boxGeometry args={[1, 1, 1]} />
      <meshBasicMaterial color="#eab308" wireframe transparent opacity={0.55} depthTest={false} />
    </mesh>
  );
}

export default function SectionGizmo() {
  const {
    enabled,
    showGizmo,
    showCaps,
    width,
    clipMode,
    pendingPoints,
    fencePoints,
    placeMode,
    orientation,
  } = useSectionStore(
    useShallow((s) => ({
      enabled: s.enabled,
      showGizmo: s.showGizmo,
      showCaps: s.showCaps,
      width: s.width,
      clipMode: s.clipMode,
      pendingPoints: s.pendingPoints,
      fencePoints: s.fencePoints,
      placeMode: s.placeMode,
      orientation: s.orientation,
    }))
  );
  const state = useSectionStore();
  const origin = getOrigin();
  const size = gizmoSize(origin);

  const planes = useMemo(
    () => (enabled ? sectionPlanes(state, origin) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      enabled,
      state.orientation,
      state.centerEast,
      state.centerNorth,
      state.centerRl,
      state.width,
      state.azimuth,
      state.clipMode,
      state.lookFlip,
      state.boxWidthEast,
      state.boxWidthNorth,
      state.boxWidthRl,
      state.fencePoints,
      state.fenceDistance,
      state.obliqueNormal,
    ]
  );

  const center = enabled ? sectionCenterScene(state, origin) : null;
  const normal = enabled ? sectionNormal({ ...state, lookFlip: false }, origin) : null;

  const pendingScene = (pendingPoints || []).map((p) => mineToScene(p.east, p.north, p.rl, origin));
  const fenceScene = (fencePoints || []).map((p) => mineToScene(p.east, p.north, p.rl, origin));

  if (!enabled && pendingScene.length === 0 && fenceScene.length === 0) return null;

  const capOpacity = showCaps ? (planes.length > 2 ? 0.1 : 0.22) : 0.05;

  return (
    <group userData={skip}>
      {showGizmo && enabled && planes.map((plane, i) => (
        <CapPlane
          key={`cap-${i}`}
          plane={plane}
          size={size}
          opacity={capOpacity}
          color={i % 2 === 0 ? '#eab308' : '#facc15'}
        />
      ))}
      {showGizmo && enabled && center && normal && clipMode !== 'single' && orientation !== 'box' && orientation !== 'object' && planes.length > 0 && (
        <EdgeBox center={center} normal={normal} width={width} size={size * 0.35} />
      )}
      {showGizmo && enabled && (orientation === 'object' || state.clipParentUuid) && (
        <ObjectClipBounds visible />
      )}
      {pendingScene.map((p, i) => (
        <mesh key={`pend-${i}`} position={p} userData={skip} raycast={() => false}>
          <sphereGeometry args={[2.2, 12, 12]} />
          <meshBasicMaterial color="#facc15" depthTest={false} />
        </mesh>
      ))}
      {pendingScene.length > 1 && (
        <Line points={pendingScene} color="#facc15" lineWidth={2} userData={skip} raycast={() => false} />
      )}
      {fenceScene.length > 1 && placeMode !== 'fence' && (
        <Line points={fenceScene} color="#eab308" lineWidth={2} userData={skip} raycast={() => false} />
      )}
      {fenceScene.map((p, i) => (
        <mesh key={`fence-${i}`} position={p} userData={skip} raycast={() => false}>
          <sphereGeometry args={[1.8, 10, 10]} />
          <meshBasicMaterial color="#ca8a04" depthTest={false} />
        </mesh>
      ))}
    </group>
  );
}
